import React from 'react';
import type { TaxImplicationResult } from '@capintel/instruments-lib';

import CurrencyCell from '@/views/components/cells/CurrencyCell';
import FormattedMessageV2 from '@/views/components/i18n/FormattedMessageV2';
import { RowMeta } from '../TaxAnalysisImplicationsSections';

type TableRow = {
  label: React.ReactNode;
  key: string;
  meta?: RowMeta;
  cell?: (props: { taxAnalysisData?: TaxImplicationResult }) => React.ReactNode;
};

export function getShortTermRows(): TableRow[] {
  return [
    {
      label: <FormattedMessageV2 className="label-header" toLocalize="Comparison.TaxAnalysis.ImplicationsTable.ShortTerm.Gains" />,
      key: 'Comparison.TaxAnalysis.ImplicationsTable.ShortTerm.Gains',
      meta: { isSubRow: true },
      cell: ({ taxAnalysisData }) => {
        const value = taxAnalysisData?.shortTerm?.gains;
        return value == null ? '-' : <CurrencyCell value={{ value }} />;
      },
    },
    {
      label: <FormattedMessageV2 className="label-header" toLocalize="Comparison.TaxAnalysis.ImplicationsTable.ShortTerm.Loss" />,
      key: 'Comparison.TaxAnalysis.ImplicationsTable.ShortTerm.Loss',
      meta: { isLossRow: true, isSubRow: true },
      cell: ({ taxAnalysisData }) => {
        const value = taxAnalysisData?.shortTerm?.losses;
        return value == null ? '-' : <CurrencyCell value={{ value }} />;
      },
    },
    {
      label: <FormattedMessageV2 className="label-header" toLocalize="Comparison.TaxAnalysis.ImplicationsTable.ShortTerm.Total" />,
      key: 'Comparison.TaxAnalysis.ImplicationsTable.ShortTerm.Total',
      meta: { isLossRow: true },
      cell: ({ taxAnalysisData }) => {
        const value = taxAnalysisData?.shortTerm?.totalGainLoss;
        return value == null ? '-' : <CurrencyCell value={{ value }} />;
      },
    },
  ];
}

export function getLongTermRows(): TableRow[] {
  return [
    {
      label: <FormattedMessageV2 className="label-header" toLocalize="Comparison.TaxAnalysis.ImplicationsTable.LongTerm.Gains" />,
      key: 'Comparison.TaxAnalysis.ImplicationsTable.LongTerm.Gains',
      meta: { isSubRow: true },
      cell: ({ taxAnalysisData }) => {
        const value = taxAnalysisData?.longTerm?.gains;
        return value == null ? '-' : <CurrencyCell value={{ value }} />;
      },
    },
    {
      label: <FormattedMessageV2 className="label-header" toLocalize="Comparison.TaxAnalysis.ImplicationsTable.LongTerm.Loss" />,
      key: 'Comparison.TaxAnalysis.ImplicationsTable.LongTerm.Loss',
      meta: { isLossRow: true, isSubRow: true },
      cell: ({ taxAnalysisData }) => {
        const value = taxAnalysisData?.longTerm?.losses;
        return value == null ? '-' : <CurrencyCell value={{ value }} />;
      },
    },
    {
      label: <FormattedMessageV2 className="label-header" toLocalize="Comparison.TaxAnalysis.ImplicationsTable.LongTerm.Total" />,
      key: 'Comparison.TaxAnalysis.ImplicationsTable.LongTerm.Total',
      meta: { isLossRow: true },
      cell: ({ taxAnalysisData }) => {
        const value = taxAnalysisData?.longTerm?.totalGainLoss;
        return value == null ? '-' : <CurrencyCell value={{ value }} />;
      },
    },
  ];
}

export function getTotalEstimatedTaxesRow(): TableRow[] {
  return [
    {
      label: <FormattedMessageV2 className="section-header" toLocalize="Comparison.TaxAnalysis.ImplicationsTable.CapitalGainLossNet" />,
      key: 'Comparison.TaxAnalysis.ImplicationsTable.CapitalGainLossNet',
      cell: ({ taxAnalysisData }) => {
        const value = taxAnalysisData?.capitalGainLossNet;
        return value == null ? '-' : <CurrencyCell value={{ value }} />;
      },
    },
    {
      label: <FormattedMessageV2 className="section-header" toLocalize="Comparison.TaxAnalysis.ImplicationsTable.CapitalGainNetOfCarryforward" />,
      key: 'Comparison.TaxAnalysis.ImplicationsTable.CapitalGainNetOfCarryforward',
      meta: { hasSectionDivider: true },
      cell: ({ taxAnalysisData }) => {
        const value = taxAnalysisData?.capitalGainNetOfCarryforward;
        return value == null ? '-' : <CurrencyCell value={{ value }} />;
      },
    },
    {
      label: <FormattedMessageV2 className="section-header" toLocalize="Comparison.TaxAnalysis.ImplicationsTable.TotalEstimatedTaxes" />,
      key: 'Comparison.TaxAnalysis.ImplicationsTable.TotalEstimatedTaxes',
      meta: { hasSectionDivider: true, isTall: true },
      cell: ({ taxAnalysisData }) => {
        const value = taxAnalysisData?.totalEstimatedTaxes;
        return value == null ? '-' : <CurrencyCell value={{ value }} />;
      },
    },
  ];
}

export function getCarryForwardRows(): TableRow[] {
  return [
    {
      label: <FormattedMessageV2 className="label-header" toLocalize="Comparison.TaxAnalysis.ImplicationsTable.CarryForward.ShortTerm" />,
      key: 'Comparison.TaxAnalysis.ImplicationsTable.CarryForward.ShortTerm',
      cell: ({ taxAnalysisData }) => {
        const value = taxAnalysisData?.carryforwardBalance?.shortTerm;
        return value == null ? '-' : <CurrencyCell value={{ value }} />;
      },
    },
    {
      label: <FormattedMessageV2 className="label-header" toLocalize="Comparison.TaxAnalysis.ImplicationsTable.CarryForward.LongTerm" />,
      key: 'Comparison.TaxAnalysis.ImplicationsTable.CarryForward.LongTerm',
      meta: { isLossRow: true },
      cell: ({ taxAnalysisData }) => {
        const value = taxAnalysisData?.carryforwardBalance?.longTerm;
        return value == null ? '-' : <CurrencyCell value={{ value }} />;
      },
    },
  ];
}
